<template>
  <div>
    <!-- Mobile Navigation Bar -->
    <nav class="navbar-mobile d-md-none">
      <div class="navbar-brand">
        <img src="@/assets/logo.png" alt="AutoOperator" class="logo" @click="goHome">
      </div>
      <button class="hamburger" :class="{ active: menuOpen }" @click="toggleMenu">
        <span></span>
        <span></span>
        <span></span>
      </button>
    </nav>

    <!-- Mobile Menu Backdrop -->
    <transition name="fade">
      <div v-if="menuOpen" class="mobile-menu-backdrop d-md-none" @click="closeMenu"></div>
    </transition>

    <!-- Mobile Menu Drawer -->
    <div class="mobile-menu-drawer d-md-none" :class="{ open: menuOpen }">
      <div class="menu-header">
        <h3>Navigation</h3>
        <div class="menu-header-actions">
          <ImageVisibilityToggle />
          <ThemeToggle />
          <button class="close-btn" @click="closeMenu">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>

      <div class="menu-user" v-if="user">
        <div class="user-avatar">{{ user.username.charAt(0).toUpperCase() }}</div>
        <div class="user-info">
          <div class="user-name">{{ user.username }}</div>
          <div class="user-role">{{ userRole }}</div>
        </div>
      </div>

      <div class="menu-items">
        <!-- Manager Menu -->
        <div v-if="isManager" class="menu-section">
          <h4 class="menu-section-title">Manager</h4>
          <router-link to="/messageEditing" class="menu-item" @click="closeMenu">
            <span class="menu-icon">✏️</span>
            Message Editing
          </router-link>
          <router-link :to="{ name: 'supervisor-userStats' }" class="menu-item" @click="closeMenu">
            <span class="menu-icon">📊</span>
            Statistics
          </router-link>
        </div>

        <!-- Supervisor Menu -->
        <div v-if="isSupervisor || isDeveloper" class="menu-section">
          <h4 class="menu-section-title">Supervisor</h4>

          <!-- Show supervisor tabs when on supervisor page (mobile only) -->
          <template v-if="isOnSupervisorPage && isMobile">
            <router-link
              :to="{ name: 'supervisor-pipeline' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'pipeline' }"
              @click="closeMenu"
            >
              <span class="menu-icon">🔀</span>
              Pipeline
            </router-link>
            <router-link
              :to="{ name: 'supervisor-voiceStudio' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'voiceStudio' }"
              @click="closeMenu"
            >
              <span class="menu-icon">🎙️</span>
              Voice Studio
            </router-link>
            <router-link
              :to="{ name: 'supervisor-traffic' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'traffic' }"
              @click="closeMenu"
            >
              <span class="menu-icon">🌐</span>
              Traffic
            </router-link>
            <router-link
              :to="{ name: 'supervisor-viewRecent' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'viewRecent' }"
              @click="handleViewRecentClick"
            >
              <span class="menu-icon">🕐</span>
              View Recent
            </router-link>
            <router-link
              :to="{ name: 'supervisor-aiConfig' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'aiConfig' }"
              @click="closeMenu"
            >
              <span class="menu-icon">⚙️</span>
              AI Config
            </router-link>
            <router-link
              :to="{ name: 'supervisor-samplerConfig' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'samplerConfig' }"
              @click="closeMenu"
            >
              <span class="menu-icon">🎛️</span>
              Sampler Config
            </router-link>
            <router-link
              :to="{ name: 'supervisor-newsConfig' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'newsConfig' }"
              @click="closeMenu"
            >
              <span class="menu-icon">📰</span>
              News Config
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-clientPolicy' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'clientPolicy' }"
              @click="closeMenu"
            >
              <span class="menu-icon">🛡️</span>
              Client Policy
            </router-link>
            <router-link
              :to="{ name: 'supervisor-ngramHeat' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'ngramHeat' }"
              @click="closeMenu"
            >
              <span class="menu-icon">🔥</span>
              Ngram Heat
            </router-link>
            <router-link
              :to="{ name: 'supervisor-operatorStats' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'operatorStats' }"
              @click="closeMenu"
            >
              <span class="menu-icon">📈</span>
              Operator Stats
            </router-link>
            <router-link
              :to="{ name: 'supervisor-userStats' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'userStats' }"
              @click="closeMenu"
            >
              <span class="menu-icon">📊</span>
              User Statistics
            </router-link>
            <router-link
              :to="{ name: 'supervisor-pokes' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'pokes' }"
              @click="closeMenu"
            >
              <span class="menu-icon">👉</span>
              Pokes
            </router-link>
            <router-link
              :to="{ name: 'supervisor-dpoStats' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'dpoStats' }"
              @click="closeMenu"
            >
              <span class="menu-icon">📈</span>
              DPO Stats
            </router-link>
            <router-link
              :to="{ name: 'supervisor-weeklyDpoStats' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'weeklyDpoStats' }"
              @click="closeMenu"
            >
              <span class="menu-icon">📅</span>
              Weekly Stats
            </router-link>
            <router-link
              :to="{ name: 'supervisor-monthlyEarnings' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'monthlyEarnings' }"
              @click="closeMenu"
            >
              <span class="menu-icon">💰</span>
              Monthly Earnings
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-devDashboard' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'devDashboard' }"
              @click="closeMenu"
            >
              <span class="menu-icon">🛠️</span>
              Dev Dashboard
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-modelTemplates' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'modelTemplates' }"
              @click="closeMenu"
            >
              <span class="menu-icon">🤖</span>
              Model Templates
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-datasetGeneration' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'datasetGeneration' }"
              @click="closeMenu"
            >
              <span class="menu-icon">📦</span>
              Dataset Generation
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-training' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'training' }"
              @click="closeMenu"
            >
              <span class="menu-icon">🎓</span>
              Training
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-testing' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'testing' }"
              @click="closeMenu"
            >
              <span class="menu-icon">🧪</span>
              Testing
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-pokeTesting' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'pokeTesting' }"
              @click="closeMenu"
            >
              <span class="menu-icon">👉</span>
              Poke Testing
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-testSuite' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'testSuite' }"
              @click="closeMenu"
            >
              <span class="menu-icon">📋</span>
              Test Suite
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-replayComparison' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'replayComparison' }"
              @click="closeMenu"
            >
              <span class="menu-icon">🔄</span>
              Replay Comparison
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-chatViewer' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'chatViewer' }"
              @click="closeMenu"
            >
              <span class="menu-icon">💭</span>
              Chat Viewer
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-datasetBuilder' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'datasetBuilder' }"
              @click="closeMenu"
            >
              <span class="menu-icon">🏗️</span>
              Dataset Builder
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-translationProfiles' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'translationProfiles' }"
              @click="closeMenu"
            >
              <span class="menu-icon">🈳</span>
              Translation Profiles
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-chatExplorer' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'chatExplorer' }"
              @click="closeMenu"
            >
              <span class="menu-icon">💬</span>
              Chat Explorer
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-imageExplorer' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'imageExplorer' }"
              @click="closeMenu"
            >
              <span class="menu-icon">🖼️</span>
              Image Explorer
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-memoryExplorer' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'memoryExplorer' }"
              @click="closeMenu"
            >
              <span class="menu-icon">🧠</span>
              Memory Explorer
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-newsExplorer' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'newsExplorer' }"
              @click="closeMenu"
            >
              <span class="menu-icon">🗞️</span>
              News Explorer
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-inferenceEndpoints' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'inferenceEndpoints' }"
              @click="closeMenu"
            >
              <span class="menu-icon">🔌</span>
              Inference Endpoints
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-futurePlans' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'futurePlans' }"
              @click="closeMenu"
            >
              <span class="menu-icon">🔮</span>
              Future Plans
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-clusterStatus' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'clusterStatus' }"
              @click="closeMenu"
            >
              <span class="menu-icon">🖥️</span>
              Cluster Status
            </router-link>
            <router-link
              v-if="isDeveloper"
              :to="{ name: 'supervisor-payloadSchema' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'payloadSchema' }"
              @click="closeMenu"
            >
              <span class="menu-icon">📋</span>
              Payload Schema
            </router-link>
            <router-link
              :to="{ name: 'supervisor-account' }"
              class="menu-item"
              :class="{ 'active-supervisor-tab': currentSupervisorTab === 'account' }"
              @click="closeMenu"
            >
              <span class="menu-icon">👤</span>
              Account
            </router-link>
          </template>

          <!-- Show regular menu link when not on supervisor page -->
          <template v-else>
            <router-link to="/supervisor" class="menu-item" @click="closeMenu">
              <span class="menu-icon">👁️</span>
              Dashboard
            </router-link>
          </template>
        </div>

        <!-- Observer Menu -->
        <div v-if="isObserver" class="menu-section">
          <h4 class="menu-section-title">Observer</h4>
          <router-link to="/observerPage" class="menu-item" @click="closeMenu">
            <span class="menu-icon">👀</span>
            Observer Dashboard
          </router-link>
        </div>

        <!-- Common Menu Items -->
        <div class="menu-section">
          <h4 class="menu-section-title">Account</h4>
          <router-link to="/account" class="menu-item" @click="closeMenu">
            <span class="menu-icon">👤</span>
            My Account
          </router-link>
          <button class="menu-item logout" @click="logout">
            <span class="menu-icon">🚪</span>
            Logout
          </button>
        </div>
      </div>
    </div>

    <!-- Desktop Navigation (existing) -->
    <nav class="navbar-desktop d-none d-md-block">
      <slot name="desktop-nav"></slot>
    </nav>
  </div>
</template>

<script>
import ThemeToggle from './ThemeToggle.vue';
import ImageVisibilityToggle from './ImageVisibilityToggle.vue';
import { resetDeveloperCache } from '../router';

export default {
  name: 'MobileNavigation',
  components: {
    ThemeToggle,
    ImageVisibilityToggle
  },
  data() {
    return {
      menuOpen: false,
      user: null,
      windowWidth: window.innerWidth
    };
  },
  computed: {
    isManager() {
      return this.user?.is_manager || false;
    },
    isSupervisor() {
      return this.user?.is_supervisor || false;
    },
    isDeveloper() {
      return this.user?.is_developer || false;
    },
    isObserver() {
      return this.user?.is_observer || false;
    },
    userRole() {
      if (this.isDeveloper) return 'Developer';
      if (this.isSupervisor) return 'Supervisor';
      if (this.isManager) return 'Manager';
      if (this.isObserver) return 'Observer';
      return 'User';
    },
    isOnSupervisorPage() {
      return this.$route?.path.startsWith('/supervisor');
    },
    currentSupervisorTab() {
      return this.$route?.meta?.tabName || '';
    },
    isMobile() {
      // Check if we're on mobile (viewport width < 768px)
      return this.windowWidth < 768;
    }
  },
  methods: {
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
      // Prevent body scroll when menu is open
      if (this.menuOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },
    closeMenu() {
      this.menuOpen = false;
      document.body.style.overflow = '';
    },
    goHome() {
      this.$router.push('/');
      this.closeMenu();
    },
    handleViewRecentClick() {
      // Dispatch event to close existing WebSocket connections
      window.dispatchEvent(new CustomEvent('tabChanged', {
        detail: { currentTab: 'viewRecent' }
      }));
      this.closeMenu();
    },
    async loadUser() {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/users/current_user/`, {
            headers: {
              'Authorization': `Token ${token}`
            }
          });
          if (response.ok) {
            this.user = await response.json();
          }
        } catch (error) {
          console.error('Error loading user:', error);
        }
      }
    },
    logout() {
      localStorage.removeItem('token');
      resetDeveloperCache(); // Clear the developer cache on logout
      this.$router.push('/');
      this.closeMenu();
    }
  },
  mounted() {
    this.loadUser();
    // Listen for window resize
    this.handleResize = () => {
      this.windowWidth = window.innerWidth;
    };
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    document.body.style.overflow = '';
    window.removeEventListener('resize', this.handleResize);
  }
};
</script>

<style scoped>
.navbar-mobile {
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--bg-primary);
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: var(--shadow-sm);
  border-bottom: 1px solid var(--border-light);
  height: 60px;
}

.navbar-brand {
  display: flex;
  align-items: center;
}

.logo {
  height: 35px;
  cursor: pointer;
}

.mobile-menu-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  backdrop-filter: blur(2px);
}

.mobile-menu-drawer {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 280px;
  background: var(--bg-primary);
  z-index: 1000;
  transform: translateX(-100%);
  transition: transform 0.3s ease;
  overflow-y: auto;
  box-shadow: var(--shadow-lg);
}

.mobile-menu-drawer.open {
  transform: translateX(0);
}

.menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-default);
  background: var(--bg-secondary);
}

.menu-header h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.2rem;
}

.menu-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.close-btn {
  background: transparent;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md, 8px);
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 150ms ease;
}

.close-btn:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
  border-color: var(--border-strong);
}

.menu-user {
  display: flex;
  align-items: center;
  padding: 20px;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-default);
}

.user-avatar {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: var(--color-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.2rem;
  margin-right: 15px;
}

.user-info {
  flex: 1;
}

.user-name {
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1.1rem;
}

.user-role {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-top: 2px;
}

.menu-items {
  padding: 15px 0;
}

.menu-section {
  margin-bottom: 25px;
}

.menu-section-title {
  color: var(--text-secondary);
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  margin: 0;
  padding: 10px 20px;
  letter-spacing: 0.5px;
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  color: var(--text-primary);
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;
  font-size: 1rem;
}

.menu-item:hover, .menu-item:active {
  background: var(--bg-hover);
  color: var(--color-primary);
  text-decoration: none;
}

.menu-item.router-link-active {
  background: rgba(var(--color-primary-rgb), 0.1);
  color: var(--color-primary);
  border-left: 3px solid var(--color-primary);
}

.menu-icon {
  margin-right: 15px;
  font-size: 1.2rem;
  width: 25px;
  text-align: center;
}

.menu-item.logout {
  color: var(--error-color);
}

.menu-item.logout:hover {
  background: rgba(207, 102, 121, 0.1);
}

/* Active supervisor tab styling */
.menu-item.active-supervisor-tab {
  background: rgba(var(--color-primary-rgb), 0.1);
  color: var(--color-primary);
  border-left: 3px solid var(--color-primary);
  font-weight: 600;
}

/* Hamburger styles - highly visible button */
.hamburger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 8px;
  background: var(--color-primary);
  border: none;
  border-radius: var(--radius-md, 8px);
  position: relative;
  z-index: 101;
  width: 44px;
  height: 44px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
}

.hamburger:hover {
  background: var(--color-primary-hover, #00796b);
  transform: scale(1.05);
}

.hamburger:active {
  transform: scale(0.95);
}

.hamburger span {
  display: block;
  width: 22px;
  height: 2px;
  background: white;
  margin: 4px 0;
  transition: 0.3s;
  border-radius: 2px;
}

.hamburger.active {
  background: var(--bg-tertiary);
}

.hamburger.active span {
  background: var(--text-primary);
}

.hamburger.active span:nth-child(1) {
  transform: rotate(-45deg) translate(-5px, 5px);
}

.hamburger.active span:nth-child(2) {
  opacity: 0;
}

.hamburger.active span:nth-child(3) {
  transform: rotate(45deg) translate(-5px, -5px);
}

/* Transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Desktop navigation placeholder */
.navbar-desktop {
  /* Existing desktop styles will be preserved */
}
</style>
